// WorkspaceThreads: the workspace mode's sidebar, its sessions kept the way a
// chat harness keeps its threads. Every agent session is one thread: newest
// first under the day it was last active, the workspaces and the pinned ones
// on top, one search over them all (the query language, each clause asked of
// the whole session). Clicking a session opens it on a workspace of its own,
// or brings it into view when it is already on the open one; its + (or a drag
// onto the board) lays it on the open workspace instead, beside what is there.
// While the pane worked in shows the range's board instead of a workspace, a
// click opens the session there all the same, with no workspace to lay it on.
//
// It is the app's sidebar like any other mode's: its rows are the sidebar's
// rows (FolderListRow's layout, titles over two lines), the date range and the
// collapse control are the shared topbar's, and what it opens is the store's
// open workspace (lib/traceCanvasStore.js), drawn in the main column.
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import QueryBar from "./QueryBar.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import ScrollArea from "./ScrollArea.jsx";
import { requestFreshChat } from "./CanvasChat.jsx";
import KernelWidget from "./KernelWidget.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import { AgentIcon } from "./Dimension.jsx";
import { NavSectionLabel } from "./ScopeSidebar.jsx";
import { useChatSessions, useTitles } from "./WorkspaceSidebar.jsx";
import { countText, hitsText, matchNote, useFlagged } from "./WorkspaceAddOverlay.jsx";
import { useKeyedReads } from "../lib/keyedReads.js";
import { useDimensionEntry } from "../lib/dimensionDefs.js";
import { SESSION_DRAG_TYPE, setDragItem } from "../lib/dragTypes.js";
import { deriveAgentIdentity } from "../lib/agentIdentity.js";
import { suggestValues } from "../lib/queryLangSuggest.js";
import { compilePipeline, looksLikePipeline, pipelineFilterText } from "../lib/queryPipeline.js";
import { SESSION_MATCH_CAP, kernelOverSessions } from "../lib/sessionMatch.js";
import { QUEUE_SECTIONS, queuePlace, useFleetFindings } from "../lib/fleetFindings.js";
import { SEVERITY_COLOR, behaviourTone } from "../lib/traceReview.js";
import { requestWorkspaceFocus, useWorkspaceCanvas } from "../lib/workspaceStore.js";
import {
  closeTraceCanvas, deleteTraceCanvas, holdsText, layOnTraceCanvas, newTraceCanvas, openTraceCanvas, previewTraceCanvas, reopenTraceCanvas, restoreTraceCanvas, traceCanvasOpen, useTraceCanvas, useTraceCanvasOpen, useTraceCanvases,
} from "../lib/traceCanvasStore.js";
import { showWorkspaceInDeck, startWorkspaceInDeck, useDeckBlankInFront } from "../lib/deckRequests.js";
import { setWorkspacePicks, useSessionMatch, useWorkspacePicks, useWorkspaceSearch } from "../lib/workspaceSearch.js";
import { splitReview } from "../lib/reviewClause.js";

const PAGE = 80;
const DAY = 86_400_000;
const DOT = " \u00b7 ";
const PIN_KEY = "origin-workspace-pins-v1";
const NONE = [];
// What a session carries when it is dragged: onto the board, it is laid on.
export { SESSION_DRAG_TYPE };

const SEV_WORDS = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };
// How long a deleted workspace's Undo stays in the list.
const DELETE_UNDO_MS = 8000;
const ICON_BUTTON = "grid h-[20px] w-[20px] shrink-0 place-items-center rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] [&_svg]:h-[11px] [&_svg]:w-[11px]";
// The sidebar's row: a 14px mark, the title over up to two lines with its
// note under it, the time on the right. Lit while it is what the workspace
// shows.
// Lit (the workspace in front, a session on it, one picked) as the app's rail
// lights its mode: the green-soft fill and a green bar down the left.
const rowClass = (active, cursor) => `relative grid w-full items-start gap-x-2 py-2 pl-[6px] pr-[9px] text-left [grid-template-columns:14px_minmax(0,1fr)_auto] ${
  active ? "bg-[color:var(--green-soft)] shadow-[inset_2px_0_0_var(--green)] hover:bg-[color:color-mix(in_srgb,var(--green)_18%,transparent)]" : cursor ? "bg-[color:var(--gray-fill)]" : "hover:bg-[color:var(--gray-fill)]"
}`;
// Set as the lists in the main column set a name and the line under it: the
// name in ink at the regular weight, what and where under it in the muted
// tone, when in the softer one beside the name's first line. A name's two
// lines sit close (15px) and what is under it a little apart, so a row reads
// as a name with a note, not as three lines. A title breaks inside a word too
// long for its line (a tool call, a path), rather than running past the
// column.
const TITLE = "text-[12px] leading-[15px] [overflow-wrap:anywhere] text-[color:var(--ink)]";
const NOTE = "mt-[4px] text-[11px] leading-[14px] text-[color:var(--muted)]";
const WHEN = "self-start whitespace-nowrap text-[11px] leading-[15px] tabular-nums text-[color:var(--muted-soft)]";

// The search without where review left a session, which the list applies
// itself (lib/reviewClause.js): what the search box has notes about.
const reviewRest = (text) => splitReview(text).rest;
// Its review clauses, chips in the box as the language's own clauses are.
const REVIEW_FIELDS = ["review"];

// The list scrolls down only: nothing in it is wider than the column.
const LIST_SCROLL = Object.freeze({ overflow: { x: "hidden" } });

// Asks the list to take the reader's search: the board's + Add, from the
// main column, finds here.
const searchListeners = new Set();
export function requestThreadsSearch() {
  for (const fn of searchListeners) fn();
}

const startOfDay = (ms) => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

// The day a session was last active, under the headings a chat harness
// files its threads by.
export function dayGroup(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return { key: "undated", label: "Undated" };
  const today = startOfDay(now);
  if (ms >= today) return { key: "today", label: "Today" };
  if (ms >= today - DAY) return { key: "yesterday", label: "Yesterday" };
  if (ms >= today - 6 * DAY) return { key: "week", label: "Previous 7 days" };
  if (ms >= today - 29 * DAY) return { key: "month", label: "Previous 30 days" };
  const d = new Date(ms);
  return { key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleDateString(undefined, { month: "long", year: "numeric" }) };
}

// When, as briefly as reads: minutes within the hour, the time today, the
// weekday this week, the date before. The formats are made once: a list
// says when for every row.
const AT_TIME = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
const ON_WEEKDAY = new Intl.DateTimeFormat(undefined, { weekday: "short" });
const ON_DATE = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
export function shortWhen(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return "";
  if (now - ms < 3_600_000 && now >= ms) return `${Math.max(1, Math.round((now - ms) / 60_000))}m`;
  const today = startOfDay(now);
  if (ms >= today) return AT_TIME.format(ms);
  if (ms >= today - 6 * DAY) return ON_WEEKDAY.format(ms);
  return ON_DATE.format(ms);
}

const readPins = () => {
  try {
    const pins = JSON.parse(globalThis.localStorage?.getItem(PIN_KEY) || "[]");
    return Array.isArray(pins) ? pins.filter((p) => p && typeof p.id === "string") : [];
  } catch {
    return [];
  }
};
const writePins = (pins) => {
  try { globalThis.localStorage?.setItem(PIN_KEY, JSON.stringify(pins)); } catch { /* this tab only */ }
};

// The agent a session ran, by its mark, as the app's tables show agents.
function AgentMark({ agent }) {
  const identity = agent ? deriveAgentIdentity(agent) : null;
  return (
    <span className="mt-[1px] grid h-[14px] w-[14px] place-items-center text-[color:var(--muted)]" aria-hidden="true" data-thread-agent={identity?.key || ""}>
      {agent ? <AgentIcon name={agent} className="h-[12px] w-[12px]" /> : <span className="h-[4px] w-[4px] rounded-full bg-[color:var(--muted-soft)]" />}
    </span>
  );
}

// A workspace's mark: the rail's own workspace icon.
function BoardMark() {
  return (
    <span className="mt-[1px] grid h-[14px] w-[14px] place-items-center text-[color:var(--muted)]" aria-hidden="true">
      <svg viewBox="0 0 16 16" className="h-[12px] w-[12px]" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
        <path d="M4.5 6h5M6.5 9.5h5" />
      </svg>
    </span>
  );
}

// How bad a flagged session's findings are, in the review's colours.
function FlagPill({ sev, findings }) {
  const word = SEV_WORDS[sev];
  if (!word) return null;
  const tone = behaviourTone(sev);
  return (
    <span className="inline-flex h-[15px] shrink-0 items-center rounded-[2px] border border-current px-[4px] text-[10.5px] leading-none" style={{ color: tone === "calm" ? "var(--muted)" : SEVERITY_COLOR[tone] }}>
      {findings ? `${word}${DOT}${findings} finding${findings === 1 ? "" : "s"}` : word}
    </span>
  );
}

function PinGlyph({ on }) {
  return (
    <svg viewBox="0 0 16 16" fill={on ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 2.5h4l-.6 3.8 2.6 2.2v1.2H4v-1.2l2.6-2.2L6 2.5zM8 9.7V14" />
    </svg>
  );
}

function TrashGlyph() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5" />
    </svg>
  );
}

function PlusGlyph() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true"><path d="M8 3.5v9M3.5 8h9" /></svg>;
}

// One thread: who ran it and what it was, where and when; what the review
// flagged in it and what the query found. Lists elsewhere show their sessions
// with it too (without `onPin`, a row has no pin). `on` marks it as on the
// workspace, or, with `onLabel`, as whatever the list holds it for (picked
// into the search).
export function Thread({ item, active, on, onLabel = "On the workspace", cursor, pinned, onOpen, onLay, onPin, onPoint }) {
  const machine = useDimensionEntry(item.endpointId ? "endpoints" : null, item.endpointId);
  const where = machine?.label && machine.label !== item.endpointId ? machine.label : "";
  const note = [where, item.note || ""].filter(Boolean).join(DOT);
  const drag = (e) => setDragItem(e, { kind: "session", id: item.id, title: item.title });
  return (
    <div role="option" aria-selected={active} className="group relative" onMouseMove={onPoint} data-thread={item.id}>
      <button type="button" draggable onDragStart={drag} onClick={onOpen} tabIndex={-1} className={rowClass(active, cursor)} data-thread-open="">
        <AgentMark agent={item.agent} />
        <span className="grid min-w-0 gap-0">
          <span data-thread-title=""><OverflowTooltip text={item.title} lines={2} className={TITLE} /></span>
          {note || item.sev || on ? (
            <span className={`${NOTE} flex min-w-0 items-center gap-[5px]`}>
              {on ? <span className="h-[6px] w-[6px] shrink-0 rounded-full bg-[color:var(--green)]" aria-label={onLabel} /> : null}
              {item.sev ? <FlagPill sev={item.sev} findings={item.findings} /> : null}
              <span className="min-w-0 truncate">{note}</span>
            </span>
          ) : null}
          {item.hits ? <span className={`${NOTE} truncate`} data-thread-hits="">{item.hits}</span> : null}
        </span>
        <span className={`${WHEN} group-hover:invisible`} data-thread-when="">{shortWhen(item.lastMs)}</span>
      </button>
      <span className="absolute right-[6px] top-[6px] hidden items-center gap-[3px] group-hover:flex">
        {onLay ? (
          <HoverTooltip placement="bottom" label="Lay it on this workspace, beside what is there">
            <button type="button" onClick={onLay} aria-label="Lay it on this workspace" className={ICON_BUTTON} data-thread-lay="">
              <PlusGlyph />
            </button>
          </HoverTooltip>
        ) : null}
        {onPin ? (
          <HoverTooltip placement="bottom" label={pinned ? "Unpin" : "Pin it to the top"}>
            <button type="button" onClick={onPin} aria-label={pinned ? "Unpin" : "Pin it to the top"} aria-pressed={pinned} className={ICON_BUTTON} data-thread-pin="">
              <PinGlyph on={pinned} />
            </button>
          </HoverTooltip>
        ) : null}
      </span>
    </div>
  );
}

// A row of the list, drawn again only when what it shows changes: typing in
// the search above redraws the list on every key, and a hundred rows drawn
// again each time made the typing lag. `actions` are the list's own, stable
// across its renders, each told which row.
const ThreadRow = memo(function ThreadRow({ item, at, active, cursor, pinned, canLay, actions }) {
  return (
    <Thread
      item={item}
      active={active}
      cursor={cursor}
      pinned={pinned}
      onOpen={() => actions.open(item, at)}
      onLay={canLay ? () => actions.lay(item) : null}
      onPin={() => actions.pin(item)}
      onPoint={() => actions.point(at)}
    />
  );
});

// A workspace kept in this browser: opened by a click, or dragged into
// another one to go inside it; deleted from the button the pointer finds in
// place of its time.
function WorkspaceRow({ c, active, cursor, onOpen, onDelete = null }) {
  return (
    <div role="option" aria-selected={active} className="group relative" data-thread-workspace={c.id}>
      <button type="button" draggable onDragStart={(e) => setDragItem(e, { kind: "workspace", id: c.id, title: c.title })} onClick={onOpen} tabIndex={-1} className={rowClass(active, cursor)}>
        <BoardMark />
        <span className="grid min-w-0 gap-0">
          <OverflowTooltip text={c.title || "Untitled workspace"} lines={2} className={TITLE} />
          <span className={NOTE}>
            {[holdsText(c), c.chat ? "from a chat" : ""].filter(Boolean).join(DOT)}
          </span>
        </span>
        <span className={`${WHEN} ${onDelete ? "group-hover:invisible" : ""}`}>{shortWhen(c.at)}</span>
      </button>
      {onDelete ? (
        <span className="absolute right-[6px] top-[6px] hidden items-center gap-[3px] group-hover:flex">
          <HoverTooltip placement="bottom" label="Delete this workspace: its sessions stay where they are, and its chat in your chats">
            <button type="button" onClick={onDelete} aria-label="Delete this workspace" className={ICON_BUTTON} data-thread-delete="">
              <TrashGlyph />
            </button>
          </HoverTooltip>
        </span>
      ) : null}
    </div>
  );
}

// A section of the list, its label staying in view while its rows scroll.
function Heading({ children }) {
  return (
    <div className="sticky top-0 z-[2] bg-[color:var(--bg)] pt-[6px]" role="presentation">
      <NavSectionLabel>{children}</NavSectionLabel>
    </div>
  );
}

function Quiet({ children }) {
  return <p className="m-0 px-[6px] py-[12px] text-[12px] leading-[1.45] text-[color:var(--muted)]">{children}</p>;
}

const inField = (t) => !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ""));

export default function WorkspaceThreads({ range = "1M" }) {
  const open = useTraceCanvasOpen();
  const c = useTraceCanvas(open?.id || null);
  const canvases = useTraceCanvases();
  const openId = open?.id || null;
  const anchorId = open ? c.anchor : null;
  // Every session on the workspace in front, whatever started it: its anchor
  // if it has one, those laid on, and those of the findings kept on it.
  const onBoard = useMemo(() => (open ? [c.anchor, ...c.layered.map((t) => t.id), ...(c.findings || []).map((f) => f.session)] : NONE), [open, c.anchor, c.layered, c.findings]);

  const [source, setSource] = useState("all");
  // The workspace's one search: the overview on an empty board reads it too.
  const [text, setText] = useWorkspaceSearch();
  const blank = useDeckBlankInFront();
  const [picks] = useWorkspacePicks();
  const [q, setQ] = useState(() => pipelineFilterText(text));
  const [pipe, setPipe] = useState(null);
  const [limit, setLimit] = useState(PAGE);
  const [pins, setPins] = useState(readPins);
  const [allWorkspaces, setAllWorkspaces] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(pipelineFilterText(text));
      setPipe(looksLikePipeline(text) ? compilePipeline(text, { scopeMode: "scoped" }) : null);
    }, 400);
    return () => clearTimeout(t);
  }, [text]);
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  useEffect(() => { setLimit(PAGE); setCursor(-1); }, [source, q, rangeKey]);

  const focusSearch = useCallback(() => {
    setSource("all");
    searchRef.current?.querySelector(".cm-content, input")?.focus();
  }, []);
  useEffect(() => {
    searchListeners.add(focusSearch);
    return () => { searchListeners.delete(focusSearch); };
  }, [focusSearch]);
  // `/` finds, from anywhere on the page that is not a field.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey || inField(e.target)) return;
      // Not from under a dialog over the page (a session previewed).
      if (document.querySelector('[aria-modal="true"]')) return;
      e.preventDefault();
      focusSearch();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusSearch]);

  const suggest = useCallback((field, term) => suggestValues(field, term, { scopePath: [], range }), [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const on = useMemo(() => new Set(onBoard.filter(Boolean).map((id) => String(id).toLowerCase())), [onBoard]);
  const isOn = useCallback((id) => on.has(String(id || "").toLowerCase()), [on]);
  const match = useSessionMatch(range, q, { enabled: source === "all" || !!q || !!pipe?.kernel });
  const matched = match.data?.sessions || NONE;
  const clauses = match.data?.clauses || NONE;
  // The sidebar's flags wait behind the workspace in front.
  const flagged = useFlagged(range);
  // Where each flagged session stands in triage, from its findings' verdicts
  // and the standards they met.
  const fleet = useFleetFindings(range);
  const triage = useMemo(() => {
    const by = new Map();
    for (const f of fleet.findings) {
      if (!by.has(f.session)) by.set(f.session, []);
      by.get(f.session).push(f);
    }
    return by;
  }, [fleet.findings]);
  const canvas = useWorkspaceCanvas();
  const related = useMemo(() => (canvas?.related || []).filter((r) => !r.kgOnly).sort((a, b) => b.evidence - a.evidence || b.shared - a.shared).slice(0, 200), [canvas?.related]);
  const chat = useChatSessions();
  // The search narrows every list, not only All: a list keeps the sessions
  // the search matches. While the range's match holds every session it
  // reaches (under its cap), it answers for every list; past the cap, the
  // lists' own sessions are matched, a few hundred at most.
  const listIds = useMemo(() => (q ? [...new Set([...flagged.sessions.map((x) => x.id), ...related.map((r) => r.id), ...chat.map((x) => String(x.id).toLowerCase())])] : NONE), [q, flagged.sessions, related, chat]);
  const ownMatch = !!q && !!match.data?.capped && !match.loading && listIds.length > 0;
  const listScope = useMemo(() => (ownMatch ? [{ dim: "sessions", ids: listIds, id: listIds[0] }] : undefined), [ownMatch, listIds]);
  const listMatch = useSessionMatch(range, q, { scopePath: listScope, enabled: ownMatch });
  const reached = useMemo(() => {
    if (!q || match.loading || !match.data) return null;
    const of = (rows) => new Map(rows.map((x) => [x.id, x]));
    if (!match.data.capped) return of(match.data.sessions);
    // Past the cap with no list holding a session, there is nothing to reach.
    if (!listIds.length) return of(NONE);
    return listMatch.data && !listMatch.loading ? of(listMatch.data.sessions) : null;
  }, [q, match.loading, match.data, listIds.length, listMatch.loading, listMatch.data]);
  // How many of a list the search reaches, once it is known.
  const countIn = (ids) => {
    const n = !q ? ids.length : reached ? ids.filter((id) => reached.has(id)).length : null;
    return n == null ? null : countText(n);
  };
  const pinned = useMemo(() => new Set(pins.map((p) => p.id)), [pins]);
  // What the review flagged, on whichever list a session turns up: the way a
  // chat harness marks the threads waiting on you.
  const flags = useMemo(() => new Map(flagged.sessions.filter((s) => s.sev && s.sev !== "other").map((s) => [s.id, s])), [flagged.sessions]);

  // The threads of the source, newest first, before their heads are read.
  // Flagged, each under where review left it, newest flagged first (its
  // newest review): how bad its worst finding is, its badge's colour says.
  const items = useMemo(() => {
    if (source === "flagged") {
      const order = new Map(QUEUE_SECTIONS.map(([id], i) => [id, i]));
      return flagged.sessions.map((s) => {
        const own = triage.get(s.id) || [];
        const met = [...new Set(own.flatMap((f) => f.met))];
        return {
          id: s.id, title: s.summary, sev: s.sev === "other" ? null : s.sev, findings: s.findings,
          lastMs: s.latestAt, firstMs: s.firstAt, endpointId: s.endpointId,
          place: queuePlace(own), note: met.length ? `Met ${met.join(", ")}` : s.titles[0] || "",
        };
      }).sort((a, b) => order.get(a.place) - order.get(b.place) || (b.lastMs || 0) - (a.lastMs || 0));
    }
    if (source === "related") {
      return related.map((r) => ({
        id: r.id, lastMs: r.lastMs ?? r.firstMs, firstMs: r.firstMs, endpointId: r.endpointId,
        note: r.shared ? `${r.shared} strings shared with the anchor` : "shares the anchor's content",
      })).sort((a, b) => (b.lastMs || 0) - (a.lastMs || 0));
    }
    if (source === "chat") {
      return chat.map((s) => ({ id: String(s.id).toLowerCase(), note: s.claims ? `${s.claims} claim${s.claims === 1 ? "" : "s"} rest on it` : "named in the chat" }));
    }
    return matched.map((s) => ({
      id: s.id, agent: s.agent, endpointId: s.endpointId, lastMs: s.lastMs, firstMs: s.firstMs, hits: hitsText(s, clauses),
    }));
  }, [source, flagged.sessions, triage, related, chat, matched, clauses]);
  // A list other than All, as far as the search reaches into it, each with
  // what the search found in it.
  const reachedItems = useMemo(() => {
    if (source === "all" || !q) return items;
    if (!reached) return NONE;
    return items.filter((it) => reached.has(it.id)).map((it) => ({ ...it, hits: hitsText(reached.get(it.id), clauses) }));
  }, [source, q, items, reached, clauses]);

  // On the open list's first page the pinned threads and the workspaces stand
  // apart, on top.
  const browsing = source === "all" && !q;
  const listed = useMemo(() => (browsing ? reachedItems.filter((it) => !pinned.has(it.id)) : reachedItems), [browsing, reachedItems, pinned]);
  const page = listed.slice(0, limit);
  const pinnedShown = browsing ? pins : NONE;
  const headIds = useMemo(() => [...pinnedShown.map((p) => p.id), ...page.map((it) => it.id)], [pinnedShown, page]);
  // Read per session and shared with the board's lists: a page scrolled
  // into view, or a search narrowed, reads only the sessions not read yet.
  const heads = useKeyedReads("getSessionHeads", headIds, { range, whole: source === "all" && !!match.data?.prefiltered });
  const titles = useTitles(source === "related" || source === "chat" ? page.map((it) => it.id) : NONE, null);
  const full = useCallback((it) => {
    const head = heads.data?.[it.id] || null;
    const flag = flags.get(it.id);
    return {
      ...it,
      sev: it.sev ?? flag?.sev ?? null,
      findings: it.findings ?? flag?.findings ?? 0,
      title: it.title || head?.summary || titles.get(it.id) || `Session ${it.id.slice(0, 8)}`,
      // Its own title, when it has one yet: what a pick keeps.
      named: it.title || head?.summary || titles.get(it.id) || "",
      agent: it.agent || head?.provider || "",
      endpointId: it.endpointId || head?.endpointId || "",
      // A flagged one says when it was flagged, the time its list is in
      // order of; any other, when it was last active.
      lastMs: source === "flagged" ? it.lastMs ?? head?.lastMs : head?.lastMs ?? it.lastMs,
      firstMs: head?.firstMs ?? it.firstMs,
    };
  }, [heads.data, titles, flags, source]);
  const shown = useMemo(() => page.map(full), [page, full]);
  const pinnedRows = useMemo(() => pinnedShown.map((p) => full({ ...p, title: heads.data?.[p.id]?.summary || p.title })), [pinnedShown, full, heads.data]);
  // A workspace of one session and nothing else is that session's, listed
  // with the sessions, unless the reader saved it; any holding more, or
  // started from no session, is one of the reader's.
  const kept = useMemo(() => (browsing ? canvases.filter((k) => k.count > 1 || k.chat || !k.anchor || k.notes || k.searches || k.machines || k.nested || k.saved) : NONE), [browsing, canvases]);
  const keptShown = allWorkspaces ? kept : kept.slice(0, 3);
  // What the workspace shows is lit once: its own row when it is listed with
  // the workspaces, else its anchor's.
  const openListed = keptShown.some((k) => k.id === openId);

  // Every row the keys step through, in the order drawn.
  const entries = useMemo(() => [
    ...keptShown.map((k) => ({ kind: "workspace", c: k })),
    ...pinnedRows.map((item) => ({ kind: "session", item })),
    ...shown.map((item) => ({ kind: "session", item })),
  ], [keptShown, pinnedRows, shown]);

  // A workspace from the search as the board starts one: its clauses and the
  // sessions picked into it.
  const startFromSearch = () => {
    const id = newTraceCanvas({ title: q || picks[0]?.title || "", searches: q ? [{ q, label: q }] : [], sessions: picks });
    if (!id) return;
    setWorkspacePicks([]);
    if (!showWorkspaceInDeck(id)) reopenTraceCanvas(id);
  };

  const togglePin = (item) => {
    const next = pinned.has(item.id) ? pins.filter((p) => p.id !== item.id) : [{ id: item.id, title: item.title, agent: item.agent || "", lastMs: item.lastMs ?? null, endpointId: item.endpointId || "" }, ...pins];
    setPins(next);
    writePins(next);
  };
  // A session opened is previewed: with the home's panes on screen, over them
  // (its Mount opens it in the pane); otherwise its board, over
  // the page. One already on the open workspace is brought into view there
  // instead.
  const openSession = useCallback((item) => {
    if (isOn(item.id)) requestWorkspaceFocus(item.id);
    else previewTraceCanvas({ id: item.id, title: item.title });
  }, [isOn]);
  // The workspaces deleted from the list: each gone at once, with an Undo
  // of its own in the list until DELETE_UNDO_MS after it went, the newest
  // first. Another deleted meanwhile keeps every earlier one's Undo.
  const [gone, setGone] = useState([]);
  useEffect(() => {
    if (!gone.length) return undefined;
    const due = Math.min(...gone.map((g) => g.at)) + DELETE_UNDO_MS - Date.now();
    const t = setTimeout(() => setGone((cur) => cur.filter((g) => Date.now() - g.at < DELETE_UNDO_MS)), Math.max(0, due));
    return () => clearTimeout(t);
  }, [gone]);
  const deleteKept = (k) => {
    const rec = deleteTraceCanvas(k.id);
    if (rec) setGone((cur) => [{ id: k.id, rec, title: k.title || "Untitled workspace", at: Date.now() }, ...cur.filter((g) => g.id !== k.id)]);
  };
  const undoDelete = (id) => {
    const g = gone.find((x) => x.id === id);
    if (g) restoreTraceCanvas(g.id, g.rec);
    setGone((cur) => cur.filter((x) => x.id !== id));
  };
  // The workspace in front, picked again, goes back to the overview in its
  // pane, still listed here; any other opens.
  const openKept = (id) => {
    if (id === openId) {
      startWorkspaceInDeck();
      return;
    }
    if (!showWorkspaceInDeck(id)) reopenTraceCanvas(id);
  };
  const lay = useCallback((item) => {
    const cur = traceCanvasOpen();
    if (!cur) {
      openTraceCanvas({ id: item.id, title: item.title });
      return;
    }
    layOnTraceCanvas(cur.id, { id: item.id, title: item.title });
    requestWorkspaceFocus(item.id);
  }, []);
  const activate = (entry) => {
    if (!entry) return;
    if (entry.kind === "workspace") openKept(entry.c.id);
    else openSession(entry.item);
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const n = entries.length;
      if (!n) return;
      setCursor((i) => (e.key === "ArrowDown" ? Math.min(n - 1, i + 1) : Math.max(0, i - 1)));
    } else if (e.key === "Enter" && cursor >= 0) {
      e.preventDefault();
      activate(entries[cursor]);
    }
  };
  useEffect(() => {
    if (cursor < 0) return;
    listRef.current?.querySelectorAll('[role="option"]')[cursor]?.scrollIntoView?.({ block: "nearest" });
  }, [cursor]);
  // The list grows as its end comes into view.
  useEffect(() => {
    const el = endRef.current;
    if (!el || typeof IntersectionObserver === "undefined" || listed.length <= limit) return undefined;
    // The scroll area's viewport is what scrolls; the list is its content.
    const root = listRef.current?.closest("[data-overlayscrollbars-viewport]") || null;
    const io = new IntersectionObserver((seen) => { if (seen.some((s) => s.isIntersecting)) setLimit((n) => n + PAGE); }, { root, rootMargin: "240px" });
    io.observe(el);
    return () => io.disconnect();
  }, [listed.length, limit]);

  // The aggregate is drawn over every request of the matched sessions.
  const kernel = useMemo(() => {
    if (!pipe?.kernel || !match.data) return null;
    if (!clauses.length || match.data.capped) return pipe.kernel;
    return kernelOverSessions(pipe.kernel, matched.map((s) => s.id));
  }, [pipe, match.data, matched, clauses.length]);

  const sources = [
    // Every session needs no count; what a search narrows it to does.
    { id: "all", label: "All", count: match.data && clauses.length ? `${countText(matched.length)}${match.data.capped ? "+" : ""}` : null },
    { id: "flagged", label: "Triage", count: flagged.status === "done" ? countIn(flagged.sessions.map((x) => x.id)) : null },
    ...(anchorId ? [{ id: "related", label: "Related", count: canvas?.status === "done" ? countIn(related.map((r) => r.id)) : null }] : []),
    ...(chat.length ? [{ id: "chat", label: "Chat", count: countIn(chat.map((x) => String(x.id).toLowerCase())) }] : []),
  ];
  const current = sources.some((s) => s.id === source) ? source : "all";
  const note = [matchNote(clauses), ...(match.data?.warnings || []).map((w) => w.message)].filter(Boolean).join(" ");
  // Another list waits, as All does, for what the search reaches of it.
  const searching = current !== "all" && !!q && !reached;
  const loading = current === "all" ? match.loading && !match.data : searching && !match.error && !listMatch.error ? true : current === "flagged" ? flagged.status === "loading" : current === "related" ? !canvas || canvas.status === "loading" || canvas.status === "idle" : false;
  const failed = current === "all" ? !!match.error : searching ? !!(match.error || listMatch.error) : current === "flagged" ? flagged.status === "error" : current === "related" ? canvas?.status === "error" : false;

  // The sessions, under the day each was last active.
  const groups = [];
  let entryAt = keptShown.length + pinnedRows.length;
  for (const item of shown) {
    const section = current === "flagged" ? QUEUE_SECTIONS.find(([id]) => id === item.place) : null;
    const g = current === "chat" ? { key: "chat", label: "Named in the chat" } : section ? { key: section[0], label: section[1] } : dayGroup(item.lastMs);
    if (!groups.length || groups[groups.length - 1].key !== g.key) groups.push({ ...g, rows: [] });
    groups[groups.length - 1].rows.push({ item, at: entryAt++ });
  }
  // What a row does, through the list as it stands at the press.
  const latest = useRef(null);
  latest.current = { openSession, lay, togglePin, cursor };
  const actions = useMemo(() => ({
    open: (item, at) => { setCursor(at); latest.current.openSession(item); },
    lay: (item) => latest.current.lay(item),
    pin: (item) => latest.current.togglePin(item),
    point: (at) => { const c = latest.current.cursor; if (c !== -1 && c !== at) setCursor(-1); },
  }), []);
  const row = (item, at) => (
    <ThreadRow
      key={item.id}
      item={item}
      at={at}
      // On the workspace in front, lit, so it is plain what is on it already.
      active={(!openListed && item.id === anchorId) || isOn(item.id)}
      cursor={cursor === at}
      pinned={pinned.has(item.id)}
      canLay={!blank && !!openId && !isOn(item.id)}
      actions={actions}
    />
  );

  return (
    <section className="flex h-full min-h-0 min-w-0 flex-col" aria-label="Sessions" data-workspace-threads="">
      <div className="grid shrink-0 gap-[8px] pt-[8px]">
        <button
          type="button"
          onClick={() => { requestFreshChat(); closeTraceCanvas(); startWorkspaceInDeck(); }}
          className="flex items-center justify-center gap-[6px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[7px] text-[12px] leading-none text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] [&_svg]:h-[12px] [&_svg]:w-[12px]"
          data-workspace-home-new=""
        >
          <PlusGlyph />
          New workspace
        </button>
        <div ref={searchRef} data-threads-search="">
          <QueryBar value={text} onChange={setText} suggestValues={suggest} placeholder="Find sessions" label={null} withoutLocal={reviewRest} localFields={REVIEW_FIELDS} />
        </div>
        {/* One line: a narrow drawer drops the counts of the tabs not shown
            before a tab would wrap under the others. */}
        <div className="@container flex flex-wrap items-baseline gap-x-[12px] gap-y-[4px] px-[6px] text-[12px]" role="tablist" aria-label="Which sessions">
          {sources.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={current === s.id}
              onClick={() => setSource(s.id)}
              className={`whitespace-nowrap ${current === s.id ? "font-medium text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
            >
              {s.label}
              {s.count ? <span className={`ml-[5px] font-normal tabular-nums text-[color:var(--muted-soft)]${current === s.id ? "" : " @max-[250px]:hidden"}`}>{s.count}</span> : null}
            </button>
          ))}
        </div>
        {note ? <p className="m-0 px-[6px] text-[11px] leading-[1.4] text-[color:var(--muted)] [overflow-wrap:anywhere]" data-workspace-match-note="">{note}</p> : null}
        {(q || picks.length) && !pipe ? (
          <button type="button" onClick={startFromSearch} className="justify-self-start px-[6px] text-left text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-threads-start-search="">
            Start a workspace from this search
          </button>
        ) : null}
        {pipe ? (
          <div className="px-[6px]" data-workspace-aggregate="">
            {pipe.kernel ? (
              kernel ? <div className="h-[170px] min-h-0"><KernelWidget kernel={kernel} params={{ scope: [], range }} showHeader={false} /></div>
                : <p className="m-0 text-[11px] text-[color:var(--muted)]">Finding the sessions to draw it over...</p>
            ) : (
              <p className="m-0 text-[11px] leading-[1.4] text-[color:var(--muted)]">{(pipe.errors.length ? pipe.errors.map((e) => e.message) : ["Add an aggregate, such as uniq session by day."]).join(DOT)}</p>
            )}
          </div>
        ) : null}
      </div>
      {/* The drawer's own scroll area, as every mode's sidebar scrolls. */}
      <ScrollArea data-sidebar-scroll className="mt-[6px] min-h-0 flex-1" contentClassName="pb-[12px]" options={LIST_SCROLL}>
        <div
          ref={listRef}
          role="listbox"
          tabIndex={0}
          aria-label="Sessions"
          onKeyDown={onKeyDown}
          className="outline-none"
          data-threads-list=""
        >
          {/* Each section a group, so its sticky heading lets go at its end. */}
          {keptShown.length || gone.length ? (
            <div role="group" aria-label="Workspaces">
              <Heading>Workspaces</Heading>
              {gone.map((g) => (
                <div key={`gone:${g.id}`} className="flex min-w-0 items-center gap-[10px] px-[6px] py-[6px] text-[11.5px] leading-[16px]" data-thread-deleted={g.id}>
                  <span className="min-w-0 truncate text-[color:var(--muted)]">{`Deleted "${g.title}"`}</span>
                  <button type="button" onClick={() => undoDelete(g.id)} className="shrink-0 font-medium text-[color:var(--ink)] hover:text-[color:var(--green)]" data-thread-undo={g.id}>Undo</button>
                </div>
              ))}
              {keptShown.map((k, i) => <WorkspaceRow key={k.id} c={k} active={k.id === openId} cursor={cursor === i} onOpen={() => { setCursor(i); openKept(k.id); }} onDelete={() => deleteKept(k)} />)}
              {kept.length > 3 ? (
                <button type="button" onClick={() => setAllWorkspaces((x) => !x)} className="px-[6px] pb-[4px] pt-[2px] text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">
                  {allWorkspaces ? "Fewer" : `All ${kept.length} workspaces`}
                </button>
              ) : null}
            </div>
          ) : null}
          {pinnedRows.length ? (
            <div role="group" aria-label="Pinned">
              <Heading>Pinned</Heading>
              {pinnedRows.map((item, i) => row(item, keptShown.length + i))}
            </div>
          ) : null}
          {loading ? <Quiet>Finding sessions...</Quiet>
            : failed ? <Quiet>The sessions could not be read.</Quiet>
              : !shown.length ? <Quiet>{q ? "No session here matches this." : current === "related" ? "No other session shares the anchor's content." : "Nothing here in this range."}</Quiet>
                : groups.map((g) => (
                  <div key={g.key} role="group" aria-label={g.label}>
                    <Heading>{g.label}</Heading>
                    {g.rows.map(({ item, at }) => row(item, at))}
                  </div>
                ))}
          {listed.length > limit ? <div ref={endRef} className="h-[1px]" aria-hidden="true" /> : null}
          {current === "all" && match.data?.capped && listed.length <= limit ? (
            <Quiet>{`These are the newest ${countText(SESSION_MATCH_CAP)} sessions that match. Narrow the search or the range to reach the rest.`}</Quiet>
          ) : null}
        </div>
      </ScrollArea>
    </section>
  );
}
